/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Define your brand profiles", description = "Add each brand's logos, colors, typefaces, and email provider for agents to use when generating ads, images, and emails.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Define your brand profiles",
        description: "Add each brand's logos, colors, typefaces, and email provider for agents to use when generating ads, images, and emails.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Create a brand profile",
          "depth": 0,
          "id": "create-a-brand-profile"
        }, {
          "value": "Choose the default profile",
          "depth": 1,
          "id": "choose-the-default-profile"
        }, {
          "value": "Build the brand kit",
          "depth": 0,
          "id": "build-the-brand-kit"
        }, {
          "value": "Add logo files",
          "depth": 1,
          "id": "add-logo-files"
        }, {
          "value": "Add colors",
          "depth": 1,
          "id": "add-colors"
        }, {
          "value": "Add typefaces",
          "depth": 1,
          "id": "add-typefaces"
        }, {
          "value": "Set the brand's email provider",
          "depth": 0,
          "id": "set-the-brands-email-provider"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      h3: "h3",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketing teams, creative teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsxs(_components.a, {
                href: "/workspace-management/roles#agents",
                children: [_jsx(_components.strong, {
                  children: "Configure Agents"
                }), " grant"]
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Add logos, colors, and fonts that agents can use when generating ads, images, and emails."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Each brand gets a brand profile in ", _jsx(_components.strong, {
          children: "Context Hub > Brands"
        }), ". Each profile holds the brand kit: the brand's logos, colors, and typefaces. In Lifecycle Studio workspaces, you can also set the email provider the brand sends through."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-kit-overview.png",
          alt: "Brand kit for a brand profile, with a summary card, logo files, and colors"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To keep brand-specific rules separate from company-wide ones, assign files, skills, styles, and copy references to a brand with their ", _jsx(_components.strong, {
          children: "Brand profiles"
        }), " setting."]
      }), "\n", _jsxs(_components.p, {
        children: ["Image generation limits and AI disclosure defaults aren't part of the brand kit. Set them per brand in ", _jsx(_components.a, {
          href: "/context-hub/guardrails#set-ad-guardrails",
          children: "Guardrails"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-brand-profile",
        children: "Create a brand profile"
      }), "\n", _jsx(_components.p, {
        children: "Create one brand profile for each brand agents generate content for. If your company markets several brands, or one brand with distinct regional identities, give each its own profile so their logos, colors, and fonts don't mix. If everything you produce shares one identity, a single profile is enough."
      }), "\n", _jsx(_components.p, {
        children: "To create a profile:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Brands"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "New brand profile"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " that your team will recognize when choosing a brand, then select ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For your first profile, select ", _jsx(_components.strong, {
          children: "Create brand profile"
        }), " instead. Hightouch creates a profile named \"New brand profile\" and opens its name for editing."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-create-brand-profile-dialog.png",
          alt: "Create brand profile dialog with the Name field"
        })
      }), "\n", _jsx(_components.p, {
        children: "Instead of starting from a blank profile, you can share your brand's website and guidelines with the Context Agent. The agent drafts a brand profile for you to review and edit."
      }), "\n", _jsx(_components.p, {
        children: "When you have more than one profile, switch between them from the brand menu on any profile page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-profile-switcher.png",
          alt: "Brand profile menu listing Acme Outfitters and Example brand"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-the-default-profile",
        children: "Choose the default profile"
      }), "\n", _jsxs(_components.p, {
        children: ["The first profile you create becomes the default. To change it, open a profile, open its actions menu, and select ", _jsx(_components.strong, {
          children: "Set as default"
        }), ". You can't delete the default profile, so set another profile as the default first if you need to remove it."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-set-as-default.png",
          alt: "Brand profile actions menu with Set as default and Delete"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "build-the-brand-kit",
        children: "Build the brand kit"
      }), "\n", _jsx(_components.p, {
        children: "Add the logos, colors, and fonts agents should use when generating content for the brand. Fill in the brand kit before you generate content for it."
      }), "\n", _jsx(_components.h3, {
        id: "add-logo-files",
        children: "Add logo files"
      }), "\n", _jsx(_components.p, {
        children: "Add each version of the logo agents might need, such as a full lockup and a standalone mark. Add light and dark variants so agents have a logo for both light and dark backgrounds."
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Add logo"
        }), " and upload the file. Logo files can be SVG, PNG, WebP, JPG, or JPEG, up to 10 MB each, and you can add up to 10 per profile."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-logo-files.png",
          alt: "Logo files section with lockup, mark, and wordmark logos in white and green variants"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Turn on ", _jsx(_components.strong, {
          children: "Set as profile logo"
        }), " for the logo that identifies the brand profile elsewhere in Hightouch. A square (1:1) logo or icon works best."]
      }), "\n", _jsx(_components.h3, {
        id: "add-colors",
        children: "Add colors"
      }), "\n", _jsx(_components.p, {
        children: "Add the colors you want agents to use, and name each one by its role, such as a primary brand color or a background neutral."
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Add color"
        }), ", then enter the ", _jsx(_components.strong, {
          children: "Hex"
        }), " value and an optional ", _jsx(_components.strong, {
          children: "Name"
        }), ". You can edit a color's value or name directly in the list."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-colors.png",
          alt: "Colors section with six named brand colors and their hex values"
        })
      }), "\n", _jsx(_components.p, {
        children: "Each hex value and name must be unique. If Hightouch flags a duplicate, rename or remove one of the colors."
      }), "\n", _jsx(_components.h3, {
        id: "add-typefaces",
        children: "Add typefaces"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Add typeface"
        }), " and choose how to add the font. The right option depends on where the font needs to work:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Web safe font"
          }), " — Choose for a font that's already available on most devices and email clients, such as Arial. This is the most reliable choice for email body text."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Google Font"
          }), " — Choose for a free font from Google Fonts. Select the variants you need and a fallback font."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Direct font file URL"
          }), " — Choose when the font file is already hosted and agents need it for text in generated images. Enter a link that ends in .woff, .woff2, .ttf, or .otf."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email font CSS URL"
          }), " — Choose when you want a custom font in the drag-and-drop email editor. Enter a CSS URL from Google Fonts, Bunny Fonts, or Adobe Fonts, and pick a fallback font that displays when the custom font can't load."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upload font file"
          }), " — Choose when you have the font file and agents need it for text in generated images. Upload a .ttf, .otf, .woff, or .woff2 file. Agents don't add uploaded fonts to emails."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A custom font often needs two entries: an uploaded file or direct URL for generated images, and an email font CSS URL for emails."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-typefaces.png",
          alt: "Typefaces section with two Google Fonts, each showing its weights and fallback font"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-the-brands-email-provider",
        children: "Set the brand's email provider"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Email provider"
          }), " section appears only in workspaces with Lifecycle\nStudio."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Lifecycle Studio writes each email in the templating syntax of the provider it sends through. By default, every brand uses the workspace provider you ", _jsx(_components.a, {
          href: "/context-hub/connections#set-a-default-email-provider",
          children: "choose in Connected data"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Set a ", _jsx(_components.strong, {
          children: "Brand default"
        }), " only when this brand sends through a different provider than the rest of your workspace, such as a brand on its own email service provider (ESP) account or a brand that sends with Native Delivery. Leave it on ", _jsx(_components.strong, {
          children: "Use workspace default"
        }), " otherwise."]
      }), "\n", _jsxs(_components.p, {
        children: ["When the brand sends through an ESP, you can also set its ", _jsx(_components.strong, {
          children: "Conditional type"
        }), ", which controls how new dynamic-content conditions are written for this brand. The options work the same way as the ", _jsx(_components.a, {
          href: "/context-hub/connections#set-a-default-email-provider",
          children: "workspace setting"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-brand-email-provider.png",
          alt: "Email provider section with Brand default set to Native Delivery"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/creative",
            children: "Set creative direction"
          }), " to add visual styles and copy references for each brand."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Manage assets"
          }), " to add the images, templates, and product catalogs agents use to generate content."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
